import * as React from "react";
import {
    Dialog, DialogTitle, DialogContent, IconButton, Box, Typography,
    Radio, RadioGroup, FormControlLabel, FormGroup, Checkbox, TextField,
    Divider,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

/**
 * TestPreviewDialog — shows a read-only student-side preview of a test.
 *
 * Props:
 *   open        — boolean
 *   onClose     — function
 *   hasSection  — boolean
 *   sections    — array of { section_name, section_number, test_questions: [...] }
 *   sample      — array of questions (used when hasSection is false)
 *   testTitle   — string (optional)
 */
const TestPreviewDialog = ({ open, onClose, hasSection, sections = [], sample = [], testTitle }) => {

    const isFrq = (q) => q.question_type === 'frq';
    const isSa = (q) => q.question_type === 'sa';

    const renderUploadZone = (label) => (
        <Box sx={{
            border: '2px dashed rgba(0,0,0,0.25)',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            backgroundColor: 'rgba(0,0,0,0.02)',
        }}>
            <CloudUploadIcon sx={{ fontSize: 28, mb: 0.5, opacity: 0.4 }} />
            <Typography variant="body2" sx={{ opacity: 0.5 }}>
                Click or drag to upload (0/5)
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.4 }}>
                JPG, PNG, HEIC, WebP — max 10MB per file
            </Typography>
        </Box>
    );

    const renderFrqPreview = (question) => {
        if (question.frq_type === 'upload') {
            return (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Upload your work — {question.max_points || '?'} {question.max_points === 1 ? 'point' : 'points'}
                    </Typography>
                    {renderUploadZone()}
                </Box>
            );
        }

        if (question.frq_type === 'saq' && question.frq_parts?.length > 0) {
            return (
                <Box sx={{ mt: 1 }}>
                    {question.frq_parts.map((part) => (
                        <Box key={`${part.part_label}-${part.part_type || 'text'}`} sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                Part ({part.part_label}) — {part.max_points} {part.max_points === 1 ? 'point' : 'points'}
                            </Typography>
                            {part.part_type === 'upload' ? (
                                renderUploadZone()
                            ) : (
                                <TextField
                                    fullWidth multiline minRows={3} maxRows={6}
                                    placeholder={`Your answer for part (${part.part_label})...`}
                                    disabled variant="outlined"
                                    sx={{ backgroundColor: 'rgba(0,0,0,0.03)' }}
                                />
                            )}
                        </Box>
                    ))}
                </Box>
            );
        }

        // DBQ / LEQ / generic FRQ
        return (
            <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {question.frq_type === 'dbq' ? 'Document-Based Question' : question.frq_type === 'leq' ? 'Long Essay Question' : 'Free Response'}
                    {question.max_points ? ` — ${question.max_points} points` : ''}
                </Typography>
                <TextField
                    fullWidth multiline minRows={4} maxRows={10}
                    placeholder="Write your response here..."
                    disabled variant="outlined"
                    sx={{ backgroundColor: 'rgba(0,0,0,0.03)' }}
                />
            </Box>
        );
    };

    const renderSaPreview = () => (
        <Box sx={{ mt: 1 }}>
            <TextField
                fullWidth placeholder="Type your answer..."
                disabled variant="outlined" size="small"
                sx={{ backgroundColor: 'rgba(0,0,0,0.03)' }}
            />
        </Box>
    );

    const renderQuestion = (question, qIndex) => {
        const qNum = question.question_number || qIndex + 1;
        return (
            <Box key={qIndex} sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    Q{qNum}
                    {isFrq(question) && (
                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'green' }}>
                            {question.frq_type === 'upload' ? '(Image Upload)' : '(Free Response)'}
                        </Typography>
                    )}
                    {isSa(question) && (
                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'green' }}>
                            (Short Answer)
                        </Typography>
                    )}
                </Typography>

                {isFrq(question) ? (
                    renderFrqPreview(question)
                ) : isSa(question) ? (
                    renderSaPreview()
                ) : (() => {
                    const opts = question.question_options || question.choices || [];
                    return (
                        <Box sx={{ mt: 0.5, ml: 1 }}>
                            {question.is_multi_select ? (
                                <FormGroup row>
                                    {opts.map((opt, i) => (
                                        <FormControlLabel
                                            key={i}
                                            control={<Checkbox disabled />}
                                            label={opt.option}
                                        />
                                    ))}
                                </FormGroup>
                            ) : (
                                <RadioGroup row value="">
                                    {opts.map((opt, i) => (
                                        <FormControlLabel
                                            key={i}
                                            value={opt.option}
                                            control={<Radio disabled />}
                                            label={opt.option}
                                        />
                                    ))}
                                </RadioGroup>
                            )}
                        </Box>
                    );
                })()}
            </Box>
        );
    };

    const renderSectionQuestions = (sectionQuestions) => {
        return sectionQuestions.map((q, i) => renderQuestion(q, i));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Student Preview{testTitle ? ` — ${testTitle}` : ''}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
                {hasSection ? (
                    sections.map((section, sIdx) => {
                        const questions = section.test_questions || section.questions || [];
                        return (
                            <Box key={sIdx} sx={{ mb: 4 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    {section.section_name || section.name || `Section ${section.section_number || sIdx + 1}`}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2, opacity: 0.7 }}>
                                    {questions.length} questions
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                {renderSectionQuestions(questions)}
                            </Box>
                        );
                    })
                ) : (
                    <Box>
                        <Typography variant="body2" sx={{ mb: 2, opacity: 0.7 }}>
                            {sample.length} questions
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {sample.map((q, i) => renderQuestion(q, i))}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default TestPreviewDialog;
